import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { FETCH_ALLOCATION_INSTRUCTIONS } from '../../../../api/queries/allocation';
import { stagingOptions } from '../../../../constants/apollo';
import { AllocationRuleFormData, ResultVia } from '../../types';
import { Input } from './Input';
import { Textarea } from './Textarea';
import { Select } from './Select';
import { Switch } from './Switch';
import { Button } from './Button';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import {
  ConditionModal,
  FALLBACK_CAST_OPTIONS,
  FALLBACK_OPERATOR_OPTIONS,
  FALLBACK_PARAMETER_OPTIONS,
  OptionType,
} from '../modals/ConditionModal';
import { Badge } from './Badge';

const RESULT_VIA_OPTIONS: OptionType[] = [
  { value: 'Partner', label: 'Partner' },
  { value: 'Internal', label: 'Internal' },
  { value: 'Courier', label: 'Courier' },
  { value: 'InternalWarehouse', label: 'Internal Warehouse' },
];

interface RuleFormProps {
  initialData?: AllocationRuleFormData | null;
  onSave: (data: AllocationRuleFormData) => void | Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
}

export const RuleForm: React.FC<RuleFormProps> = ({
  initialData,
  onSave,
  onCancel,
  isSaving = false,
}) => {
  const [formData, setFormData] = useState<AllocationRuleFormData>(
    initialData || {
      name: '',
      priority: '',
      resultVia: 'Partner' as ResultVia,
      resultData: '',
      isActive: true,
      description: '',
      conditions: [],
    }
  );

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isConditionModalOpen, setIsConditionModalOpen] = useState(false);
  const [editingConditionIndex, setEditingConditionIndex] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isNameInputFocused, setIsNameInputFocused] = useState(false);
  const [isResultDataFocused, setIsResultDataFocused] = useState(false);
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);
  const { data: instructionsData } = useQuery(FETCH_ALLOCATION_INSTRUCTIONS, stagingOptions);

  const instructions = instructionsData?.fetchAllocationInstructions;

  const parameterOptions = useMemo<OptionType[]>(() => {
    if (!instructions?.commonParameters?.length) {
      return FALLBACK_PARAMETER_OPTIONS;
    }

    return instructions.commonParameters.map((param: any) => ({
      value: param.path,
      label: param.label ? `${param.label} (${param.path})` : param.path,
    }));
  }, [instructions]);

  const operatorOptions = useMemo<OptionType[]>(() => {
    if (!instructions?.operators?.length) {
      return FALLBACK_OPERATOR_OPTIONS;
    }

    return instructions.operators.map((operator: any) => ({
      value: operator.operator,
      label: operator.label || operator.operator,
    }));
  }, [instructions]);

  const castOptions = useMemo<OptionType[]>(() => {
    const castTypes: string[] =
      instructions?.operators
        ?.flatMap((operator: any) => operator.castTypes || [])
        .filter(Boolean) || [];

    if (castTypes.length === 0) {
      return FALLBACK_CAST_OPTIONS;
    }

    const uniqueTypes = Array.from(new Set(castTypes));

    return uniqueTypes.map((cast: string) => ({
      value: cast,
      label: cast.charAt(0).toUpperCase() + cast.slice(1),
    }));
  }, [instructions]);

  const prevInitialDataRef = React.useRef<AllocationRuleFormData | null>(null);

  useEffect(() => {
    if (initialData) {
      const hasChanged = prevInitialDataRef.current !== initialData &&
        prevInitialDataRef.current?.name !== initialData.name;

      if (!isInitialized || hasChanged) {
        setFormData(initialData);
        setErrors({});
        setIsInitialized(true);
        prevInitialDataRef.current = initialData;
      }
    }
  }, [initialData, isInitialized]);

  const handleFieldChange = (field: keyof AllocationRuleFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Rule name is required';
    } else if (formData.name.length > 256) {
      newErrors.name = 'Rule name must not exceed 256 characters';
    }

    if (formData.priority === '' || formData.priority === null || formData.priority === undefined) {
      newErrors.priority = 'Priority is required';
    } else {
      const priorityStr = String(formData.priority);
      const isInteger = /^\d+$/.test(priorityStr) && Number.isInteger(Number(priorityStr));
      if (!isInteger || isNaN(Number(formData.priority))) {
        newErrors.priority = 'Priority must be a whole number (e.g., 10, 5, 16)';
      } else {
        const priorityNum = Number(formData.priority);
        if (priorityNum < 1) {
          newErrors.priority = 'Priority must be at least 1';
        } else if (priorityNum > 10000) {
          newErrors.priority = 'Priority must not exceed 10000';
        }
      }
    }

    if (!formData.resultVia || formData.resultVia.trim() === '') {
      newErrors.resultVia = 'Result Via is required';
    }

    if (!formData.resultData || formData.resultData.trim() === '') {
      newErrors.resultData = 'Result Data is required';
    } else if (formData.resultData.length > 3000) {
      newErrors.resultData = 'Result Data must not exceed 3000 characters';
    }

    if (formData.description && formData.description.length > 3000) {
      newErrors.description = 'Description must not exceed 3000 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fix the validation errors before saving.');
      return;
    }

    onSave(formData as AllocationRuleFormData);
  };

  const handleAddCondition = (condition: { parameter: string; operator: string; value: string; castType: string; id?: string }) => {
    if (editingConditionIndex !== null) {
      const updatedConditions = [...(formData.conditions || [])];
      updatedConditions[editingConditionIndex] = { ...condition, id: updatedConditions[editingConditionIndex]?.id || Date.now().toString() };
      setFormData((prev) => ({ ...prev, conditions: updatedConditions }));
      setEditingConditionIndex(null);
    } else {
      setFormData((prev) => ({
        ...prev,
        conditions: [...(prev.conditions || []), { ...condition, id: Date.now().toString() }],
      }));
    }
    setIsConditionModalOpen(false);
  };

  const handleEditCondition = (index: number) => {
    setEditingConditionIndex(index);
    setIsConditionModalOpen(true);
  };

  const handleDeleteCondition = (index: number) => {
    const updatedConditions = formData.conditions.filter((_: any, i: number) => i !== index);
    setFormData({ ...formData, conditions: updatedConditions });
  };

  const getConditionSummary = (condition: { parameter: string; operator: string; value: string }) => {
    return `${condition.parameter} ${condition.operator} ${condition.value}`;
  };

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Input
              label="Strategy Name"
              value={formData.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (value.length <= 256) {
                  handleFieldChange('name', value);
                }
              }}
              onFocus={() => setIsNameInputFocused(true)}
              onBlur={() => setIsNameInputFocused(false)}
              placeholder="e.g., Merchant 123 → JTExpress"
              required
              error={errors.name}
              className="text-sm py-2.5"
              maxLength={256}
            />
            {(isNameInputFocused || formData.name) && (
              <p className="text-[11px] text-gray-400 mt-1 font-medium italic">
                {(formData.name || '').length}/256 characters
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:col-span-2">
            <div>
              <Input
                label="Execution Priority"
                type="text"
                value={formData.priority === '' ? '' : formData.priority.toString()}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value;
                  const numericValue = value.replace(/[^0-9]/g, '');
                  if (numericValue === '') {
                    handleFieldChange('priority', '');
                    return;
                  }
                  const priorityNum = Number(numericValue);
                  if (priorityNum > 10000) return;
                  handleFieldChange('priority', numericValue);
                }}
                placeholder="1 (Highest) - 10000"
                required
                error={errors.priority}
                className="text-sm py-2.5"
              />
              <p className="text-[11px] text-gray-400 mt-1">Lower values are evaluated first</p>
            </div>

            <div>
              <Select
                label="Result Channel Type"
                options={RESULT_VIA_OPTIONS}
                value={formData.resultVia}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => handleFieldChange('resultVia', e.target.value as ResultVia)}
                placeholder="Select channel..."
                required
                error={errors.resultVia}
                className="text-sm py-2.5"
              />
              <p className="text-[11px] text-gray-400 mt-1">Target fulfillment entity type</p>
            </div>
          </div>

          <div className="md:col-span-2">
            <Input
              label="Result Mapping Data"
              value={formData.resultData}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (value.length <= 3000) {
                  handleFieldChange('resultData', value);
                }
              }}
              onFocus={() => setIsResultDataFocused(true)}
              onBlur={() => setIsResultDataFocused(false)}
              placeholder="e.g., JTExpress_KSA"
              required
              error={errors.resultData}
              className="text-sm py-2.5"
              maxLength={3000}
            />
            <p className="text-[11px] text-gray-400 mt-1">Exact identifier for the chosen result channel</p>
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between p-4 bg-gray-50/50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${formData.isActive ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                  <div className={`w-2 h-2 rounded-full ${formData.isActive ? 'bg-green-600 animate-pulse' : 'bg-gray-400'}`} />
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-900 block">Strategy Activation</label>
                  <p className="text-xs text-gray-500 font-medium">Toggle the operational status of this logic</p>
                </div>
              </div>
              <Switch
                label=""
                checked={formData.isActive}
                onCheckedChange={(checked: boolean) =>
                  setFormData({ ...formData, isActive: checked })
                }
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <Textarea
              label="Strategy Description"
              value={formData.description}
              required={false}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const value = e.target.value;
                if (value.length <= 3000) {
                  setFormData({ ...formData, description: value });
                }
              }}
              onFocus={() => setIsDescriptionFocused(true)}
              onBlur={() => setIsDescriptionFocused(false)}
              placeholder="Provide context for this allocation rule..."
              rows={3}
              className="text-sm"
              maxLength={3000}
              error={errors.description}
            />
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-100 w-full" />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Execution Conditions</h3>
            <p className="text-sm text-gray-500 font-medium">Define when this strategy should trigger</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditingConditionIndex(null);
              setIsConditionModalOpen(true);
            }}
            className="rounded-xl border-gray-200 hover:bg-gray-50"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
              <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Add Condition
          </Button>
        </div>

        {formData.conditions.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-gray-100 rounded-2xl bg-gray-50/30">
            <p className="text-sm text-gray-500 font-medium italic">No conditions configured yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {formData.conditions.map((condition: any, index: number) => (
              <div
                key={condition.id || index}
                className="group relative flex items-start justify-between p-4 bg-white rounded-xl border border-gray-100 hover:border-primaryColor/30 hover:shadow-md transition-all duration-300"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-extrabold text-primaryColor bg-primaryColor/5 px-2 py-0.5 rounded">
                      {condition.parameter}
                    </span>
                    <span className="text-[10px] font-black text-gray-400">
                      {condition.operator}
                    </span>
                    <span className="text-[10px] font-bold text-gray-900 bg-amber-50 px-2 py-0.5 rounded">
                      {condition.value}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleEditCondition(index)}
                    className="p-1.5 text-gray-400 hover:text-primaryColor hover:bg-primaryColor/5 rounded-lg transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                  </button>
                  <button
                    onClick={() => handleDeleteCondition(index)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 pt-8 border-t border-gray-100">
        <Button
          variant="outline"
          onClick={onCancel}
          className="px-6 min-w-[120px] rounded-xl border-gray-200"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="px-8 min-w-[150px] rounded-xl shadow-lg shadow-primaryColor/20 bg-primaryColor hover:bg-primaryColor/90 text-white font-bold"
          disabled={isSaving}
        >
          {isSaving ? (
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
              Saving...
            </div>
          ) : (
            'Save Strategy'
          )}
        </Button>
      </div>

      <ConditionModal
        open={isConditionModalOpen}
        onOpenChange={setIsConditionModalOpen}
        onSave={handleAddCondition}
        initialCondition={
          editingConditionIndex !== null
            ? formData.conditions[editingConditionIndex]
            : undefined
        }
        parameterOptions={parameterOptions}
        operatorOptions={operatorOptions}
        castOptions={castOptions}
      />
    </div>
  );
};

